package com.example.readingnotes.data.repository

import com.example.readingnotes.data.remote.api.ApiClient
import com.example.readingnotes.data.remote.dto.*
import com.example.readingnotes.domain.model.*
import com.example.readingnotes.domain.repository.PaidContentRepository
import com.example.readingnotes.util.Result

class PaidContentRepositoryImpl(
    private val apiClient: ApiClient,
) : PaidContentRepository {
    override suspend fun setPostPrice(
        tenantSlug: String,
        postId: String,
        price: Int,
        currency: String,
    ): Result<PurchasableContent> =
        try {
            val request = SetPriceRequestDto(price = price, currency = currency)
            val response = apiClient.setPostPrice(tenantSlug, postId, request)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun createPurchaseSession(
        contentId: String,
        successUrl: String,
        cancelUrl: String,
    ): Result<PurchaseSession> =
        try {
            val request = CreatePurchaseRequestDto(successUrl, cancelUrl)
            val response = apiClient.createPurchaseSession(contentId, request)
            Result.success(
                PurchaseSession(
                    checkoutUrl = response.checkoutUrl,
                    sessionId = response.sessionId,
                    contentId = response.contentId,
                    amount = response.amount,
                    currency = response.currency,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getContentAccess(contentId: String): Result<ContentAccessResult> =
        try {
            val response = apiClient.getPurchaseStatus(contentId)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getUserPurchases(): Result<List<ContentPurchase>> =
        try {
            val response = apiClient.getUserPurchases()
            Result.success(response.purchases.map { it.toDomain() })
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getSalesDashboard(tenantSlug: String): Result<SalesDashboard> =
        try {
            val response = apiClient.getSalesDashboard(tenantSlug)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun refundPurchase(purchaseId: String): Result<ContentPurchase> =
        try {
            val response = apiClient.refundPurchase(purchaseId)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }
}

// Extension functions for DTO to Domain mapping
fun PurchasableContentDto.toDomain(): PurchasableContent =
    PurchasableContent(
        id = id,
        postId = postId,
        tenantId = tenantId,
        creatorId = creatorId,
        price = price,
        currency = currency,
        status = status.toPurchasableContentStatus() ?: PurchasableContentStatus.DRAFT,
        publishedAt = publishedAt?.let { kotlinx.datetime.Instant.parse(it) },
        createdAt = kotlinx.datetime.Instant.parse(createdAt),
        updatedAt = kotlinx.datetime.Instant.parse(updatedAt),
    )

fun ContentPurchaseDto.toDomain(): ContentPurchase =
    ContentPurchase(
        id = id,
        contentId = contentId,
        purchaserId = purchaserId,
        amountPaid = amountPaid,
        platformFee = platformFee,
        creatorEarnings = creatorEarnings,
        purchasedAt = kotlinx.datetime.Instant.parse(purchasedAt),
        refundedAt = refundedAt?.let { kotlinx.datetime.Instant.parse(it) },
    )

fun ContentAccessResultDto.toDomain(): ContentAccessResult =
    ContentAccessResult(
        hasAccess = hasAccess,
        isPurchased = isPurchased,
        isAuthor = isAuthor,
        price = price,
        currency = currency,
        purchaseDate = purchaseDate?.let { kotlinx.datetime.Instant.parse(it) },
    )

fun SalesDashboardDto.toDomain(): SalesDashboard =
    SalesDashboard(
        totalSales = totalSales,
        totalRevenue = totalRevenue,
        platformFees = platformFees,
        creatorEarnings = creatorEarnings,
        salesByContent = salesByContent.map { it.toDomain() },
    )

fun ContentSalesDto.toDomain(): ContentSales =
    ContentSales(
        contentId = contentId,
        postId = postId,
        title = title,
        salesCount = salesCount,
        revenue = revenue,
    )
